import { Column } from 'primereact/column';
import { DataTable, DataTableRowClickEvent, DataTableStateEvent } from 'primereact/datatable';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import EmptyMyCertificatesImg from '../../../../../../../../assets/images/empty-my-certificates.svg';
import EmptyScreen from '../../../../../../../../components/empty-screen/EmptyScreen';
import { useGetPaginatedMyCertificates } from '../../../../../../../../hooks/api/certificates';
import { useMyCertificatesTableQueryParams } from '../../../../../../../../hooks/useMyCertificatesTableQueryParams';
import { Locale } from '../../../../../../../../i18n';
import { getPaginatorTemplate } from '../../../../../../../../shared/helpers/get-paginator-template';
import { parseDateWithoutTime } from '../../../../../../../../shared/helpers/parse-date';
import { IMyCertificatesTableRow } from '../../../../../../../../shared/interfaces/ICertificate';
import CertificateStatusTag from '../../../../../certificate-status-tag/CertificateStatusTag';
import AddCertificateButton from '../add-certificate-button/AddCertificateButton';
import ManageSitesButton from '../manage-sites-button/ManageSitesButton';
import styles from './MyCertificatesTable.module.css';

const MyCertificatesTable = (): JSX.Element => {
  const { skip, size, sort, setParams } = useMyCertificatesTableQueryParams();
  const [sortField, sortOrder] = sort ? sort.split('-') : [undefined, undefined];
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const {
    i18n: { language },
    t,
  } = useTranslation();

  const navigate = useNavigate();

  const { data, isLoading } = useGetPaginatedMyCertificates({
    skip,
    size,
    sort,
  });

  useEffect(() => {
    if (data) {
      setTotalRecords(data?.pagination?.total || 0);
    }
  }, [data]);

  const handleRowClick = (event: DataTableRowClickEvent) => {
    const target = event.originalEvent.target as HTMLElement | null;
    const rowData = event.data as IMyCertificatesTableRow;

    if (target?.closest('button, a, [role="button"]')) {
      return;
    }

    navigate(`/certificates/my-certificates/${rowData.id}/details`);
  };

  const validUntilTemplate = (rowData: IMyCertificatesTableRow): React.ReactNode => {
    if (!rowData.validUntil) {
      return (
        <span className='font-italic text-gray-700'>{t('addCertificateDialog.noExpiration')}</span>
      );
    }

    return parseDateWithoutTime(rowData.validUntil, language as Locale);
  };

  const statusTemplate = (rowData: IMyCertificatesTableRow) => {
    return <CertificateStatusTag status={rowData.status} />;
  };

  const enclosedSitesTemplate = (rowData: IMyCertificatesTableRow): React.ReactNode => {
    if (rowData.appliesToAllSites) {
      return t('addCertificateDialog.inputs.companyWide');
    }

    if (!rowData.enclosedSites?.length) {
      return '-';
    }

    const [firstSite, ...remainingSites] = rowData.enclosedSites;

    if (!remainingSites.length) {
      return firstSite.name;
    }

    return `${firstSite.name} ${t(
      'certificatesPage.tabs.myCertificatesTab.myCertificatesTableColumns.moreCount',
      { count: remainingSites.length },
    )}`;
  };

  const isTableEmpty = !isLoading && data?.data.length === 0;

  if (isTableEmpty) {
    return (
      <EmptyScreen imageSrc={EmptyMyCertificatesImg}>
        <div className='flex flex-column align-items-center p-4 gap-3'>
          <h4 className='m-0 p-0'>
            {t('certificatesPage.tabs.myCertificatesTab.emptyScreen.title')}
          </h4>
          <span>{t('certificatesPage.tabs.myCertificatesTab.emptyScreen.text')}</span>
          <AddCertificateButton />
        </div>
      </EmptyScreen>
    );
  }

  return (
    <div className={styles['my-certificates-table']}>
      <DataTable
        value={data?.data}
        loading={isLoading}
        header={
          <div className='flex flex-column md:flex-row gap-2 w-full'>
            <AddCertificateButton />
            <ManageSitesButton />
          </div>
        }
        lazy
        rows={size}
        paginatorPosition='top'
        paginator
        sortField={sortField}
        sortOrder={sortField ? (sortOrder === 'desc' ? -1 : 1) : undefined}
        rowClassName={() => {
          return 'cursor-pointer my-hoverable-row';
        }}
        onRowClick={handleRowClick}
        onSort={(event: DataTableStateEvent) => {
          const newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
          setParams({
            sort: `${event.sortField}-${newSortOrder}`,
            page: 1,
          });
        }}
        scrollable
        paginatorTemplate={getPaginatorTemplate({
          totalRecords,
          rowsPerPage: size,
          skip,
          onNext: () => {
            setParams({ page: (skip + size) / size + 1 });
          },
          onPrev: () => {
            setParams({ page: (skip + size) / size - 1 });
          },
        })}
        tableStyle={{
          tableLayout: 'fixed',
          minWidth: '800px',
        }}
        emptyMessage={t('global.emptyTableMessage')}
      >
        <Column
          field='type'
          header={t(
            'certificatesPage.tabs.myCertificatesTab.myCertificatesTableColumns.certificateType',
          )}
          sortable
          style={{ width: '30%', wordBreak: 'break-word' }}
        />

        <Column
          field='enclosedSites'
          header={t(
            'certificatesPage.tabs.myCertificatesTab.myCertificatesTableColumns.enclosedSites',
          )}
          body={enclosedSitesTemplate}
          style={{ width: '40%', wordBreak: 'break-word' }}
        />

        <Column
          header={t(
            'certificatesPage.tabs.myCertificatesTab.myCertificatesTableColumns.validUntil',
          )}
          body={validUntilTemplate}
          sortable
          sortField='validUntil'
          style={{ width: '15%' }}
        />

        <Column
          header={t('certificatesPage.tabs.myCertificatesTab.myCertificatesTableColumns.status')}
          body={statusTemplate}
          sortable
          sortField='status'
          style={{ width: '15%' }}
        />
      </DataTable>
    </div>
  );
};

export default MyCertificatesTable;
